import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import frLocale from '@fullcalendar/core/locales/fr';
import { Plus, CalendarRange, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../api';
import QuickEventDialog from './QuickEventDialog';
import EventDetailDialog from './EventDetailDialog';
import { INTERVENTION_STATUS, URGENCY_LEVELS } from '../constants';

const colorFor = (type, status) => {
  if (status === 'annulee') return { bg: '#e5e7eb', border: '#d1d5db', text: '#6b7280' };
  if (type === 'pause') return { bg: '#f59e0b', border: '#d97706', text: '#ffffff' };
  if (type === 'indisponibilite') return { bg: '#6b7280', border: '#4b5563', text: '#ffffff' };
  if (['terminee', 'cloturee'].includes(status)) return { bg: '#16a34a', border: '#15803d', text: '#ffffff' };
  if (['en_cours', 'en_route', 'arrive'].includes(status)) return { bg: '#ea580c', border: '#c2410c', text: '#ffffff' };
  return { bg: '#B91C1C', border: '#991B1B', text: '#ffffff' }; // planifiée / intervention
};

const StatPill = ({ icon: Icon, label, value, accent, onClick }) => (
  <button onClick={onClick} data-testid={`agenda-stat-${label}`}
    className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:shadow-sm transition-shadow ${accent}`}>
    <Icon className="w-4 h-4" />
    <span className="text-lg font-bold">{value}</span>
    <span className="text-xs text-neutral-500 hidden sm:inline">{label}</span>
  </button>
);

const AdminAgenda = () => {
  const navigate = useNavigate();
  const calRef = useRef(null);
  const rangeRef = useRef({ start: null, end: null });
  const [events, setEvents] = useState([]);
  const [unplanned, setUnplanned] = useState([]);
  const [extended, setExtended] = useState(false); // 24h
  const [weekends, setWeekends] = useState(true);

  const [quickOpen, setQuickOpen] = useState(false);
  const [quickSlot, setQuickSlot] = useState(null);
  const [quickPrefill, setQuickPrefill] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);

  const mapEvent = (e) => {
    const c = colorFor(e.type, e.status);
    return {
      id: e.id, title: e.title, start: e.start, end: e.end,
      backgroundColor: c.bg, borderColor: c.border, textColor: c.text,
      editable: e.status !== 'annulee',
      extendedProps: {
        type: e.type, status: e.status, clientName: e.clientName,
        interventionId: e.interventionId, interventionNumber: e.interventionNumber, note: e.note,
      },
    };
  };

  const fetchEvents = useCallback(async (startIso, endIso) => {
    try {
      const { data } = await api.get(`/events?start=${startIso}&end=${endIso}`);
      setEvents(data.map(mapEvent));
    } catch (e) { /* interceptor */ }
  }, []);

  const fetchUnplanned = useCallback(async () => {
    try {
      const { data } = await api.get('/interventions?unplanned=true');
      setUnplanned(data);
    } catch (e) { /* ignore */ }
  }, []);

  const refresh = useCallback(() => {
    if (rangeRef.current.start) fetchEvents(rangeRef.current.start, rangeRef.current.end);
    fetchUnplanned();
  }, [fetchEvents, fetchUnplanned]);

  const handleDatesSet = (arg) => {
    rangeRef.current = { start: arg.start.toISOString(), end: arg.end.toISOString() };
    fetchEvents(arg.start.toISOString(), arg.end.toISOString());
    fetchUnplanned();
  };

  const handleSelect = (info) => {
    setQuickPrefill(null);
    setQuickSlot({ start: info.start.toISOString(), end: info.end.toISOString() });
    setQuickOpen(true);
    calRef.current?.getApi().unselect();
  };

  const openNew = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    const end = new Date(now.getTime() + 3600000);
    setQuickPrefill(null);
    setQuickSlot({ start: now.toISOString(), end: end.toISOString() });
    setQuickOpen(true);
  };

  const handleEventClick = (info) => {
    const p = info.event.extendedProps;
    setDetailEvent({
      id: info.event.id, title: info.event.title,
      start: info.event.start.toISOString(), end: info.event.end.toISOString(),
      type: p.type, status: p.status, clientName: p.clientName,
      interventionId: p.interventionId, interventionNumber: p.interventionNumber,
    });
    setDetailOpen(true);
  };

  const handleEventChange = async (info) => {
    try {
      await api.patch(`/events/${info.event.id}`, {
        start: info.event.start.toISOString(),
        end: info.event.end.toISOString(),
      });
      toast.success('Créneau mis à jour');
      fetchUnplanned();
    } catch (e) {
      info.revert();
      toast.error("Échec de l'enregistrement — créneau restauré");
    }
  };

  const planUnplanned = (it) => {
    const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
    const end = new Date(start.getTime() + 3600000);
    setQuickSlot({ start: start.toISOString(), end: end.toISOString() });
    setQuickPrefill({ interventionId: it.id, clientName: it.clientName, number: it.number, title: it.description });
    setQuickOpen(true);
  };

  // Compteurs barre d'état
  const now = Date.now();
  const todayStr = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
  const todayCount = events.filter((e) => e.extendedProps.type === 'intervention' && e.extendedProps.status !== 'annulee'
    && new Date(e.start).toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' }) === todayStr).length;
  const overdueCount = events.filter((e) => e.extendedProps.type === 'intervention'
    && e.extendedProps.status === 'planifiee' && new Date(e.end).getTime() < now).length;

  return (
    <div data-testid="admin-agenda">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Agenda</h1>
          <p className="text-neutral-500 text-sm">Votre poste de travail — fuseau Europe/Paris</p>
        </div>
        <Button onClick={openNew} className="bg-red-700 hover:bg-red-600" data-testid="new-intervention-button">
          <Plus className="w-4 h-4 mr-2" />Nouvelle intervention
        </Button>
      </div>

      {/* Barre d'état */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatPill icon={CalendarRange} label="À planifier" value={unplanned.length} accent="border-purple-200" onClick={() => document.getElementById('unplanned-panel')?.scrollIntoView({ behavior: 'smooth' })} />
        <StatPill icon={Clock} label="RDV aujourd'hui" value={todayCount} accent="border-blue-200" onClick={() => calRef.current?.getApi().today()} />
        <StatPill icon={AlertTriangle} label="En retard" value={overdueCount} accent="border-red-200" />
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-sm text-neutral-600 bg-white border rounded-lg px-3">
          <input type="checkbox" checked={extended} onChange={(e) => setExtended(e.target.checked)} data-testid="toggle-24h" />24h
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-600 bg-white border rounded-lg px-3">
          <input type="checkbox" checked={weekends} onChange={(e) => setWeekends(e.target.checked)} data-testid="toggle-weekends" />Week-ends
        </label>
      </div>

      {/* Panneau à planifier */}
      {unplanned.length > 0 && (
        <div id="unplanned-panel" className="bg-white rounded-xl border border-neutral-200 p-3 mb-4">
          <p className="text-xs font-semibold text-neutral-500 mb-2 flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" />Demandes à planifier ({unplanned.length})</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {unplanned.map((it) => {
              const ur = URGENCY_LEVELS[it.urgency] || {};
              return (
                <div key={it.id} className="shrink-0 w-60 border border-neutral-200 rounded-lg p-3" data-testid={`unplanned-${it.number}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-neutral-800">{it.number}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${ur.color}`}>{ur.label}</span>
                  </div>
                  <p className="text-sm font-medium text-neutral-900 truncate">{it.clientName}</p>
                  <p className="text-xs text-neutral-500 truncate mb-2">{it.description}</p>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-red-700 hover:bg-red-600 h-8" onClick={() => planUnplanned(it)} data-testid={`plan-${it.number}`}>Planifier</Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(`/admin/interventions/${it.id}`)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Calendrier */}
      <div className="bg-white rounded-xl border border-neutral-200 p-2 sm:p-3" style={{ height: 'calc(100vh - 300px)', minHeight: '480px' }}>
        <FullCalendar
          ref={calRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin, momentTimezonePlugin]}
          timeZone="Europe/Paris"
          locale={frLocale}
          initialView="timeGridWeek"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'timeGridDay,timeGridWeek,dayGridMonth' }}
          height="100%"
          allDaySlot={false}
          nowIndicator={true}
          slotDuration="00:30:00"
          slotMinTime={extended ? '00:00:00' : '07:00:00'}
          slotMaxTime={extended ? '24:00:00' : '20:00:00'}
          defaultTimedEventDuration="01:00:00"
          weekends={weekends}
          selectable={true}
          selectMirror={true}
          editable={true}
          eventStartEditable={true}
          eventDurationEditable={true}
          events={events}
          datesSet={handleDatesSet}
          select={handleSelect}
          eventClick={handleEventClick}
          eventDrop={handleEventChange}
          eventResize={handleEventChange}
          eventContent={(arg) => {
            const p = arg.event.extendedProps;
            const st = INTERVENTION_STATUS[p.status];
            return (
              <div className="px-1 py-0.5 overflow-hidden leading-tight">
                <div className="text-[11px] font-semibold">{arg.timeText}</div>
                <div className="text-[11px] font-bold truncate">{p.clientName || arg.event.title}</div>
                {p.clientName && <div className="text-[10px] truncate opacity-90">{arg.event.title}</div>}
                {st && <div className="text-[9px] uppercase tracking-wide opacity-80">{st.label}</div>}
              </div>
            );
          }}
        />
      </div>

      <QuickEventDialog open={quickOpen} onClose={() => setQuickOpen(false)} slot={quickSlot} prefill={quickPrefill} onCreated={refresh} />
      <EventDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} event={detailEvent} onChanged={refresh} />
    </div>
  );
};

export default AdminAgenda;
